"use client"

import * as React from "react"
import { OpenAppTab, InstalledApp, App } from "@/types/workspace"
import { NotificationItem } from "@/types/notification"

export function getAppSlug(app?: { slug?: string | null; id?: string } | null): string {
  if (!app) return ""
  if (app.slug && app.slug.trim()) {
    const s = app.slug.trim().toLowerCase()
    return s.startsWith("bold-") && s !== "bold-system" ? s.replace(/^bold-/, "") : s
  }
  if (app.id) {
    const id = app.id.toLowerCase()
    if (id.startsWith("bold-") && id !== "bold-system") {
      return id.replace(/^bold-/, "")
    }
    return id
  }
  return ""
}

export const SYSTEM_ROUTE_SLUGS = new Set([
  "desktop",
  "desk",
  "home",
  "profile",
  "settings",
  "marketplace",
  "new-tab",
  "system-calendar",
  "system-weather",
  "system-calculator",
  "system-notes",
  "widgets",
  "dock",
  "users",
  "workspaces",
  "developer",
  "developers",
  "sso",
  "webhooks",
  "api",
  "api-keys",
  "history",
  "registros",
  "bar",
  "topbar",
  "apps-list"
])

export function isSystemApp(app?: { id?: string; slug?: string | null; type?: string | null } | null): boolean {
  if (!app) return false
  const id = (app.id || "").toLowerCase().trim()
  const slug = (app.slug || "").toLowerCase().trim()
  if (
    id === "settings" ||
    id === "system-settings" ||
    id === "profile" ||
    id === "system-profile" ||
    id === "marketplace" ||
    id === "new-tab" ||
    id.startsWith("new-tab") ||
    id.startsWith("system-") ||
    slug === "settings" ||
    slug === "profile" ||
    slug === "marketplace" ||
    slug === "new-tab" ||
    slug.startsWith("new-tab") ||
    slug.startsWith("system-") ||
    app.type === "system"
  ) {
    return true
  }
  return false
}

export function getWorkspaceBasePath(
  account?: { id?: string; slug?: string } | null,
  workspace?: { id?: string; slug?: string } | null
): string {
  const accSlug = account?.slug || account?.id || ""
  const wsSlug = workspace?.slug || workspace?.id || ""
  if (!accSlug || !wsSlug) return "/"
  return `/account/${accSlug}/workspaces/${wsSlug}`
}

export function buildWorkspaceDesktopPath(
  account?: { id?: string; slug?: string } | null,
  workspace?: { id?: string; slug?: string } | null
): string {
  const base = getWorkspaceBasePath(account, workspace)
  if (base === "/") return "/"
  return `${base}/desktop`
}

export function buildWorkspaceAppPath(
  account?: { id?: string; slug?: string } | null,
  workspace?: { id?: string; slug?: string } | null,
  app?: { id?: string; slug?: string | null; type?: string | null } | null,
  subpath?: string
): string {
  const base = getWorkspaceBasePath(account, workspace)
  if (base === "/") return "/"
  if (!app) return `${base}/desktop`

  let cleanSub = (subpath || "").trim()
  if (cleanSub === "/" || cleanSub === "/blank" || cleanSub === "blank") cleanSub = ""
  if (cleanSub.startsWith("/")) cleanSub = cleanSub.slice(1)

  if (isSystemApp(app)) {
    const sysSlug =
      app.slug === "new-tab" || app.id?.startsWith("new-tab")
        ? "new-tab"
        : app.id === "settings" || app.id === "system-settings"
          ? "settings"
          : app.id === "profile" || app.id === "system-profile"
            ? "profile"
            : app.slug || app.id || "desktop"
    return cleanSub ? `${base}/${sysSlug}/${cleanSub}` : `${base}/${sysSlug}`
  }

  const appSlug = app.slug || getAppSlug(app) || app.id || ""
  if (!appSlug) return `${base}/desktop`
  return cleanSub ? `${base}/apps/${appSlug}/${cleanSub}` : `${base}/apps/${appSlug}`
}

export interface ParsedWorkspaceRoute {
  isAccountRoute: boolean
  accountIdentifier: string | null
  workspaceIdentifier: string | null
  isExplicitAppsRoute: boolean
  appIdentifier: string | null
  primary: string | null
  secondary: string | null
  tertiary: string | null
  appSubpath: string
}

export function parseWorkspaceRouteFromPathname(
  pathname: string,
  search = "",
  hash = ""
): ParsedWorkspaceRoute {
  const rawSegments = (pathname || "")
    .split("/")
    .filter(Boolean)
    .map((s) => {
      try {
        return decodeURIComponent(s)
      } catch {
        return s
      }
    })
  const segments = rawSegments.map((s) => s.toLowerCase())

  if (segments.length === 0) {
    return {
      isAccountRoute: false,
      accountIdentifier: null,
      workspaceIdentifier: null,
      isExplicitAppsRoute: false,
      appIdentifier: null,
      primary: null,
      secondary: null,
      tertiary: null,
      appSubpath: ""
    }
  }

  let isAccountRoute = false
  let accountIdentifier: string | null = null
  let workspaceIdentifier: string | null = null
  let routeStartIndex = 0

  if (segments[0] === "account" || segments[0] === "accounts") {
    isAccountRoute = true
    accountIdentifier = rawSegments[1] || null
    if (segments[2] === "workspaces" || segments[2] === "workspace") {
      workspaceIdentifier = rawSegments[3] || null
      routeStartIndex = 4
    } else {
      routeStartIndex = 2
    }
  } else {
    accountIdentifier = rawSegments[0] || null
    if (segments[1] === "workspaces" || segments[1] === "workspace") {
      workspaceIdentifier = rawSegments[2] || null
      routeStartIndex = 3
    } else {
      workspaceIdentifier = rawSegments[1] || null
      routeStartIndex = 2
    }
  }

  const firstRouteSeg = segments[routeStartIndex] || null
  if ((firstRouteSeg === "apps" || firstRouteSeg === "app") && rawSegments[routeStartIndex + 1]) {
    const appIdSeg = rawSegments[routeStartIndex + 1]
    const rest = rawSegments.slice(routeStartIndex + 2).join("/")
    const appSubpath = rest ? `/${rest}${search}${hash}` : `${search}${hash}`
    return {
      isAccountRoute,
      accountIdentifier,
      workspaceIdentifier,
      isExplicitAppsRoute: true,
      appIdentifier: appIdSeg,
      primary: segments[routeStartIndex + 1] || null,
      secondary: segments[routeStartIndex + 2] || null,
      tertiary: segments[routeStartIndex + 3] || null,
      appSubpath
    }
  }

  const rest = rawSegments.slice(routeStartIndex + 1).join("/")
  const appSubpath = rest ? `/${rest}${search}${hash}` : `${search}${hash}`

  return {
    isAccountRoute,
    accountIdentifier,
    workspaceIdentifier,
    isExplicitAppsRoute: false,
    appIdentifier: rawSegments[routeStartIndex] || null,
    primary: firstRouteSeg,
    secondary: segments[routeStartIndex + 1] || null,
    tertiary: segments[routeStartIndex + 2] || null,
    appSubpath
  }
}

export function isAppMatch(
  ia: { app: { id?: string; slug?: string; name?: string } },
  term: string
): boolean {
  if (!term || !ia?.app) return false
  const t = term.toLowerCase().trim()
  const slug = (ia.app.slug || "").toLowerCase().trim()
  const id = (ia.app.id || "").toLowerCase().trim()
  const name = (ia.app.name || "").toLowerCase().trim()

  if (slug === t || id === t) return true
  if (slug.replace(/^bold-/, "") === t || id.replace(/^bold-/, "") === t) return true
  if (slug === `bold-${t}` || id === `bold-${t}`) return true
  if (name.replace(/\s+/g, "-") === t || name.replace(/\s+/g, "") === t) return true
  return false
}

export function resolveIframeUrl(baseUrl: string, subpath?: string) {
  if (!subpath || subpath === "blank" || subpath === "/blank") return baseUrl
  const cleanSubpath = subpath.startsWith("/") ? subpath : "/" + subpath
  try {
    const baseObj = new URL(baseUrl)
    const basePath = baseObj.pathname.replace(/\/$/, "")
    if (basePath && cleanSubpath.startsWith(basePath)) {
      return `${baseObj.origin}${cleanSubpath}`
    }
    if (cleanSubpath.startsWith(baseObj.pathname) || baseObj.pathname === "/") {
      return `${baseObj.origin}${cleanSubpath}`
    }
    return `${baseUrl.replace(/\/$/, '')}${cleanSubpath}`
  } catch (e) {
    return `${baseUrl.replace(/\/$/, '')}${cleanSubpath}`
  }
}

export function useAppRouterSync({
  activeAppId,
  setActiveAppId,
  splitActiveAppId,
  setSplitActiveAppId,
  focusedPane,
  openApps,
  setOpenApps,
  installedApps,
  setWidgetsConfig,
  setNotifications,
  playNotificationSound,
  isMobile,
  setSettingsTab,
  currentWorkspace,
  currentAccount,
  iframeSubpaths: externalIframeSubpaths
}: {
  activeAppId: string | null
  setActiveAppId: React.Dispatch<React.SetStateAction<string | null>>
  splitActiveAppId: string | null
  setSplitActiveAppId: React.Dispatch<React.SetStateAction<string | null>>
  focusedPane: "left" | "right"
  openApps: OpenAppTab[]
  setOpenApps: React.Dispatch<React.SetStateAction<OpenAppTab[]>>
  installedApps: InstalledApp[]
  setWidgetsConfig: React.Dispatch<React.SetStateAction<any[]>>
  setNotifications: React.Dispatch<React.SetStateAction<NotificationItem[]>>
  playNotificationSound: () => void
  isMobile: boolean
  setSettingsTab: (tab: any) => void
  currentWorkspace?: any
  currentAccount?: any
  iframeSubpaths?: any
}) {
  const internalIframeSubpaths = React.useRef<Record<string, string>>({})
  const iframeSubpaths = externalIframeSubpaths || internalIframeSubpaths

  // Hydrate iframeSubpaths from openApps or localStorage on mount/workspace change
  React.useEffect(() => {
    if (typeof window === "undefined") return
    if (currentWorkspace?.id) {
      try {
        const saved = localStorage.getItem(`bold_app_subpaths_${currentWorkspace.id}`)
        if (saved) {
          const parsed = JSON.parse(saved)
          if (parsed && typeof parsed === "object") {
            Object.assign(iframeSubpaths.current, parsed)
          }
        }
      } catch (e) {}
    }
    openApps.forEach((tab) => {
      if (tab.subpath) {
        iframeSubpaths.current[tab.instanceId] = tab.subpath
      }
    })
  }, [currentWorkspace?.id, openApps])

  const updateAppSubpath = React.useCallback(
    (instanceId: string, subpath: string) => {
      let cleanSub = subpath.trim()
      if (cleanSub.includes("://") || cleanSub.startsWith("//")) {
        try {
          const u = new URL(cleanSub, window.location.origin)
          cleanSub = u.pathname + u.search + u.hash
        } catch (e) {}
      }

      const targetTab = openApps.find((t) => t.instanceId === instanceId) || openApps.find((t) => t.instanceId === activeAppId)
      if (targetTab) {
        const baseApp = installedApps.find((ia) => isAppMatch(ia, targetTab.app.slug || targetTab.app.id))?.app
        const baseIframeUrl = targetTab.baseIframeUrl || baseApp?.iframeUrl || targetTab.app.iframeUrl

      }

      if (!cleanSub.startsWith("/")) cleanSub = "/" + cleanSub
      if (cleanSub === "/" || cleanSub === "/blank") cleanSub = ""

      const effectiveInstanceId = targetTab ? targetTab.instanceId : instanceId
      const appSlug = targetTab ? getAppSlug(targetTab.app) : ""
      iframeSubpaths.current[effectiveInstanceId] = cleanSub
      if (targetTab?.app?.id) iframeSubpaths.current[targetTab.app.id] = cleanSub
      if (appSlug) iframeSubpaths.current[appSlug] = cleanSub

      if (targetTab) {
        setOpenApps((prev) =>
          prev.map((t) =>
            t.instanceId === effectiveInstanceId
              ? {
                  ...t,
                  subpath: cleanSub
                }
              : t
          )
        )

        if (typeof window !== "undefined" && currentWorkspace?.id) {
          try {
            localStorage.setItem(`bold_subpath_${currentWorkspace.id}_${targetTab.app.id}`, cleanSub)
            if (appSlug) {
              localStorage.setItem(`bold_subpath_${currentWorkspace.id}_${appSlug}`, cleanSub)
            }
            localStorage.setItem(`bold_app_subpaths_${currentWorkspace.id}`, JSON.stringify(iframeSubpaths.current))
          } catch (e) {}
        }

        if (typeof window !== "undefined" && (currentAccount?.id || currentAccount?.slug) && (currentWorkspace?.id || currentWorkspace?.slug)) {
          const isTargetActive = effectiveInstanceId === activeAppId || (!activeAppId && openApps.length === 1) || (openApps.length > 0 && targetTab.instanceId === activeAppId)
          if (isTargetActive) {
            const fullPath = buildWorkspaceAppPath(currentAccount, currentWorkspace, targetTab.app, cleanSub)
            if (window.location.pathname + window.location.search + window.location.hash !== fullPath) {
              window.history.replaceState({ path: fullPath }, "", fullPath)
            }
          }
        }
      }
    },
    [openApps, installedApps, currentWorkspace, currentAccount, activeAppId, setOpenApps]
  )

  const handleOpenApp = React.useCallback((
    app: App,
    subpath?: string,
    options?: { forceNewTab?: boolean; inBackground?: boolean; openInSplit?: boolean }
  ) => {
    const installed = installedApps.find((ia) => isAppMatch(ia, app.slug || app.id))
    if (installed && installed.status === "inactive") {
      return
    }

    const safeSlug = getAppSlug(app)
    const resolvedAppId = installed?.app?.id || app.id
    const appWithSlug = { ...app, id: resolvedAppId, slug: safeSlug }

    let cleanSubpath = ""
    if (subpath && subpath !== "blank" && subpath !== "/blank") {
      cleanSubpath = subpath.startsWith("/") ? subpath : "/" + subpath
    }

    const existing = options?.forceNewTab
      ? undefined
      : openApps.find(
          (t) => t.app.id === appWithSlug.id || isAppMatch({ app: t.app }, appWithSlug.slug)
        )

    // If subpath wasn't explicitly passed (subpath === undefined), check existing tab subpath, in-memory ref, or localStorage
    if (!cleanSubpath && subpath === undefined && !options?.forceNewTab) {
      if (existing?.subpath) {
        cleanSubpath = existing.subpath
      } else if (existing && iframeSubpaths.current[existing.instanceId]) {
        cleanSubpath = iframeSubpaths.current[existing.instanceId]
      } else if (typeof window !== "undefined" && currentWorkspace?.id) {
        cleanSubpath =
          localStorage.getItem(`bold_subpath_${currentWorkspace.id}_${safeSlug}`) ||
          localStorage.getItem(`bold_subpath_${currentWorkspace.id}_${appWithSlug.id}`) ||
          iframeSubpaths.current[appWithSlug.id] ||
          iframeSubpaths.current[safeSlug] ||
          ""
      } else {
        cleanSubpath =
          iframeSubpaths.current[appWithSlug.id] ||
          iframeSubpaths.current[safeSlug] ||
          ""
      }
    }

    const baseAppUrl = installed?.app?.iframeUrl || app.iframeUrl
    const finalIframeUrl = cleanSubpath ? resolveIframeUrl(baseAppUrl, cleanSubpath) : baseAppUrl

    if (existing) {
      if (cleanSubpath) {
        iframeSubpaths.current[existing.instanceId] = cleanSubpath
        if (typeof window !== "undefined" && currentWorkspace?.id) {
          try {
            localStorage.setItem(`bold_subpath_${currentWorkspace.id}_${appWithSlug.id}`, cleanSubpath)
            localStorage.setItem(`bold_subpath_${currentWorkspace.id}_${safeSlug}`, cleanSubpath)
            localStorage.setItem(`bold_app_subpaths_${currentWorkspace.id}`, JSON.stringify(iframeSubpaths.current))
          } catch (e) {}
        }
      }

      setOpenApps((prev) =>
        prev.map((t) =>
          t.instanceId === existing.instanceId
            ? {
                ...t,
                baseIframeUrl: baseAppUrl,
                subpath: cleanSubpath,
                app: { ...t.app, iframeUrl: finalIframeUrl }
              }
            : t
        )
      )

      try {
        const iframe = document.querySelector(`iframe[data-instance-id="${existing.instanceId}"]`) as HTMLIFrameElement
        if (iframe) {
          if (cleanSubpath && iframe.contentWindow) {
            iframe.contentWindow.postMessage({ type: "BOLD_NAVIGATE", payload: { path: cleanSubpath, url: finalIframeUrl }, path: cleanSubpath }, "*")
          }
          if (finalIframeUrl && iframe.src !== finalIframeUrl) {
            iframe.src = finalIframeUrl
          }
        }
      } catch (e) {}

      if (!options?.inBackground) {
        if (options?.openInSplit && activeAppId) {
          setSplitActiveAppId(existing.instanceId)
        } else if (splitActiveAppId !== null) {
          if (focusedPane === "left") {
            setActiveAppId(existing.instanceId)
          } else {
            setSplitActiveAppId(existing.instanceId)
          }
        } else {
          setActiveAppId(existing.instanceId)
        }
      }

      // Sync URL immediately
      if (!options?.inBackground && typeof window !== "undefined" && (currentAccount?.id || currentAccount?.slug) && (currentWorkspace?.id || currentWorkspace?.slug)) {
        const fullPath = buildWorkspaceAppPath(currentAccount, currentWorkspace, appWithSlug, cleanSubpath)
        if (window.location.pathname + window.location.search + window.location.hash !== fullPath) {
          window.history.replaceState({ path: fullPath }, "", fullPath)
        }
      }
      return
    }

    const instanceId = Math.random().toString(36).substring(2, 9)
    if (cleanSubpath) {
      iframeSubpaths.current[instanceId] = cleanSubpath
      if (typeof window !== "undefined" && currentWorkspace?.id) {
        try {
          localStorage.setItem(`bold_subpath_${currentWorkspace.id}_${appWithSlug.id}`, cleanSubpath)
          localStorage.setItem(`bold_subpath_${currentWorkspace.id}_${safeSlug}`, cleanSubpath)
          localStorage.setItem(`bold_app_subpaths_${currentWorkspace.id}`, JSON.stringify(iframeSubpaths.current))
        } catch (e) {}
      }
    }

    const newTab: OpenAppTab = {
      instanceId,
      subpath: cleanSubpath,
      baseIframeUrl: baseAppUrl,
      app: {
        ...appWithSlug,
        iframeUrl: finalIframeUrl
      },
      pinned: false,
      groupId: null
    }

    setOpenApps((prev) => [...prev, newTab])

    if (!options?.inBackground) {
      if (options?.openInSplit && activeAppId) {
        setSplitActiveAppId(instanceId)
      } else if (splitActiveAppId !== null) {
        if (focusedPane === "left") {
          setActiveAppId(instanceId)
        } else {
          setSplitActiveAppId(instanceId)
        }
      } else {
        setActiveAppId(instanceId)
      }
    }

    // Sync URL immediately
    if (!options?.inBackground && typeof window !== "undefined" && (currentAccount?.id || currentAccount?.slug) && (currentWorkspace?.id || currentWorkspace?.slug)) {
      const fullPath = buildWorkspaceAppPath(currentAccount, currentWorkspace, appWithSlug, cleanSubpath)
      if (window.location.pathname + window.location.search + window.location.hash !== fullPath) {
        window.history.replaceState({ path: fullPath }, "", fullPath)
      }
    }
  }, [openApps, activeAppId, splitActiveAppId, focusedPane, setOpenApps, setActiveAppId, setSplitActiveAppId, resolveIframeUrl, installedApps, currentWorkspace, currentAccount])

  const handleOpenWidgetAsApp = React.useCallback((widgetId: string, name: string, icon: string) => {
    const existing = openApps.find(t => t.app.id === widgetId)
    if (existing) {
      setActiveAppId(existing.instanceId)
      return
    }
    const dummyApp: App = {
      id: widgetId,
      name: name,
      slug: widgetId,
      description: `Widget ${name} do Sistema`,
      iconUrl: `emoji:${icon}`,
      iframeUrl: ""
    }
    const instanceId = Math.random().toString(36).substring(2, 9)
    const newTab: OpenAppTab = {
      instanceId,
      app: dummyApp,
      pinned: false,
      groupId: null
    }
    setOpenApps((prev) => [...prev, newTab])
    setActiveAppId(instanceId)
    setSplitActiveAppId(null)
  }, [openApps, setActiveAppId, setOpenApps, setSplitActiveAppId])

  const handleCloseApp = React.useCallback((instanceId: string) => {
    if (iframeSubpaths.current) {
      delete iframeSubpaths.current[instanceId]
    }

    const nextApps = openApps.filter((a) => a.instanceId !== instanceId)
    setOpenApps(nextApps)

    if (activeAppId === instanceId) {
      if (nextApps.length > 0) {
        setActiveAppId(nextApps[nextApps.length - 1].instanceId)
      } else {
        setActiveAppId(null)
      }
    }

    if (splitActiveAppId === instanceId) {
      setSplitActiveAppId(null)
    }
  }, [openApps, activeAppId, splitActiveAppId, setActiveAppId, setSplitActiveAppId, setOpenApps])

  const handleCloseAllApps = React.useCallback(() => {
    if (iframeSubpaths.current) {
      iframeSubpaths.current = {}
    }
    setOpenApps([])
    setActiveAppId(null)
    setSplitActiveAppId(null)
  }, [setActiveAppId, setOpenApps, setSplitActiveAppId])

  const handleOpenSettingsTab = React.useCallback(() => {
    const existingTab = openApps.find((t) => t.app.id === "settings")
    setSettingsTab(isMobile ? "menu" : "company")
    if (existingTab) {
      if (splitActiveAppId !== null) {
        if (focusedPane === "left") {
          setActiveAppId(existingTab.instanceId)
        } else {
          setSplitActiveAppId(existingTab.instanceId)
        }
      } else {
        setActiveAppId(existingTab.instanceId)
      }
    } else {
      const settingsApp: App = {
        id: "settings",
        name: "Ajustes do Sistema",
        slug: "settings",
        description: "Configurações globais do sistema",
        iconUrl: "",
        iframeUrl: ""
      }
      const newTab: OpenAppTab = {
        instanceId: "settings",
        app: settingsApp,
        pinned: false,
        groupId: null
      }
      setOpenApps((prev) => (prev.some((t) => t.app.id === "settings") ? prev : [...prev, newTab]))
      if (splitActiveAppId !== null) {
        if (focusedPane === "left") {
          setActiveAppId("settings")
        } else {
          setSplitActiveAppId("settings")
        }
      } else {
        setActiveAppId("settings")
      }
    }
  }, [openApps, splitActiveAppId, focusedPane, isMobile, setSettingsTab, setActiveAppId, setSplitActiveAppId, setOpenApps])

  const handleOpenProfileTab = React.useCallback(() => {
    const existingTab = openApps.find((t) => t.app.id === "profile")
    if (existingTab) {
      if (splitActiveAppId !== null) {
        if (focusedPane === "left") {
          setActiveAppId(existingTab.instanceId)
        } else {
          setSplitActiveAppId(existingTab.instanceId)
        }
      } else {
        setActiveAppId(existingTab.instanceId)
      }
    } else {
      const profileApp: App = {
        id: "profile",
        name: "Meu Perfil",
        slug: "profile",
        description: "Gerenciamento de perfil e credenciais",
        iconUrl: "emoji:👤",
        iframeUrl: ""
      }
      const newTab: OpenAppTab = {
        instanceId: "profile",
        app: profileApp,
        pinned: false,
        groupId: null
      }
      setOpenApps((prev) => (prev.some((t) => t.app.id === "profile") ? prev : [...prev, newTab]))
      if (splitActiveAppId !== null) {
        if (focusedPane === "left") {
          setActiveAppId("profile")
        } else {
          setSplitActiveAppId("profile")
        }
      } else {
        setActiveAppId("profile")
      }
    }
  }, [openApps, splitActiveAppId, focusedPane, setActiveAppId, setSplitActiveAppId, setOpenApps])

  const handleOpenMarketplaceTab = React.useCallback(() => {
    const existingTab = openApps.find((t) => t.app.id === "marketplace")
    if (existingTab) {
      if (splitActiveAppId !== null) {
        if (focusedPane === "left") {
          setActiveAppId(existingTab.instanceId)
        } else {
          setSplitActiveAppId(existingTab.instanceId)
        }
      } else {
        setActiveAppId(existingTab.instanceId)
      }
    } else {
      const marketplaceApp: App = {
        id: "marketplace",
        name: "Loja de Aplicativos",
        slug: "marketplace",
        description: "Catálogo de aplicativos e extensões da plataforma Bold",
        iconUrl: "emoji:🛒",
        iframeUrl: ""
      }
      const newTab: OpenAppTab = {
        instanceId: "marketplace",
        app: marketplaceApp,
        pinned: false,
        groupId: null
      }
      setOpenApps((prev) => (prev.some((t) => t.app.id === "marketplace") ? prev : [...prev, newTab]))
      if (splitActiveAppId !== null) {
        if (focusedPane === "left") {
          setActiveAppId("marketplace")
        } else {
          setSplitActiveAppId("marketplace")
        }
      } else {
        setActiveAppId("marketplace")
      }
    }
  }, [openApps, splitActiveAppId, focusedPane, setActiveAppId, setSplitActiveAppId, setOpenApps])

  const handleAddNewTab = React.useCallback(() => {
    const instanceId = `new-tab-${Math.random().toString(36).substring(2, 9)}`
    const newTabApp: App = {
      id: instanceId,
      name: "Nova Guia",
      slug: "new-tab",
      description: "Nova Guia de Trabalho",
      iconUrl: "",
      iframeUrl: ""
    }
    const newTab: OpenAppTab = {
      instanceId,
      app: newTabApp,
      pinned: false,
      groupId: null
    }
    setOpenApps((prev) => [...prev, newTab])
    if (splitActiveAppId !== null) {
      if (focusedPane === "left") {
        setActiveAppId(instanceId)
      } else {
        setSplitActiveAppId(instanceId)
      }
    } else {
      setActiveAppId(instanceId)
    }
  }, [setOpenApps, splitActiveAppId, focusedPane, setActiveAppId, setSplitActiveAppId])

  const handleOpenExternalUrl = React.useCallback((
    rawUrl: string,
    options?: { forceNewTab?: boolean; inBackground?: boolean; openInSplit?: boolean; sourceBaseUrl?: string }
  ) => {
    if (!rawUrl || typeof rawUrl !== "string") return

    let targetUrl = rawUrl.trim()
    if (!targetUrl || targetUrl === "about:blank" || targetUrl.startsWith("javascript:") || targetUrl.startsWith("#")) return

    if (targetUrl.startsWith("//")) {
      targetUrl = "https:" + targetUrl
    }

    const forceNewTab = options?.forceNewTab ?? true

    // 1. Verificar se a URL corresponde a um app instalado e ativo ou a uma rota interna do Bold Workspace
    let matchedInstalledApp: InstalledApp | undefined = undefined
    let extractedSubpath = ""
    let isInternalWorkspaceUrl = false

    try {
      const currentOrigin = typeof window !== "undefined" ? window.location.origin : "https://boldworkspace.com.br"
      const baseResolutionUrl = options?.sourceBaseUrl || (typeof window !== "undefined" ? window.location.href : currentOrigin)
      const parsedTarget = new URL(targetUrl, baseResolutionUrl)
      targetUrl = parsedTarget.href

      // 1.1 Rota interna do Bold Workspace: /account/:accId/workspaces/:wsId/... ou /:accSlug/:wsSlug/:appSlug/...
      const isInternalWorkspace = parsedTarget.origin === currentOrigin || (typeof window !== "undefined" && parsedTarget.host === window.location.host)
      isInternalWorkspaceUrl = isInternalWorkspace
      if (isInternalWorkspace) {
        const parsedRoute = parseWorkspaceRouteFromPathname(parsedTarget.pathname, parsedTarget.search, parsedTarget.hash)
        const candidateSlug = (parsedRoute.appIdentifier || parsedRoute.primary || "").toLowerCase()
        if (candidateSlug) {
          if (candidateSlug === "desktop") {
            handleAddNewTab()
            return
          }
          if (candidateSlug === "settings" || candidateSlug === "configuracoes") {
            handleOpenSettingsTab()
            return
          }
          if (candidateSlug === "profile" || candidateSlug === "perfil") {
            handleOpenProfileTab()
            return
          }
          if (candidateSlug === "marketplace" || candidateSlug === "loja") {
            handleOpenMarketplaceTab()
            return
          }
          if (candidateSlug === "new-tab") {
            handleAddNewTab()
            return
          }

          const matched = installedApps.find((ia) => {
            if (ia.status === "inactive") return false
            const appSlug = ia.app?.slug || ia.app?.id
            return (
              ia.app?.id?.toLowerCase() === candidateSlug ||
              isAppMatch(ia, candidateSlug) ||
              appSlug?.toLowerCase() === candidateSlug
            )
          })

          if (matched) {
            matchedInstalledApp = matched
            extractedSubpath = parsedRoute.appSubpath
          }
        }
      }

      // 1.2 Verificar se o destino corresponde ao iframeUrl de algum app instalado e ativo
      if (!matchedInstalledApp) {
        for (const ia of installedApps) {
          if (ia.status === "inactive" || !ia.app?.iframeUrl) continue
          try {
            const parsedApp = new URL(ia.app.iframeUrl)
            if (parsedApp.origin.toLowerCase() === parsedTarget.origin.toLowerCase()) {
              const appBasePath = parsedApp.pathname.replace(/\/+$/, "")
              const targetPath = parsedTarget.pathname.replace(/\/+$/, "")

              if (!appBasePath || appBasePath === "" || targetPath.startsWith(appBasePath)) {
                matchedInstalledApp = ia
                let sub = targetPath.slice(appBasePath.length) + parsedTarget.search + parsedTarget.hash
                if (sub && !sub.startsWith("/")) {
                  sub = "/" + sub
                }
                extractedSubpath = sub
                break
              }
            }
          } catch (e) {}
        }
      }
    } catch (e) {
      // Formato inválido de URL
    }

    // 2. Se for de algum app já instalado e ativo, considera-o e abre o app em uma nova guia (quando forceNewTab = true)
    if (matchedInstalledApp) {
      handleOpenApp(matchedInstalledApp.app, extractedSubpath, {
        forceNewTab,
        inBackground: options?.inBackground,
        openInSplit: options?.openInSplit
      })
      return
    }

    // Evita abrir o próprio Bold Workspace recursivamente dentro de um iframe externo
    if (isInternalWorkspaceUrl) {
      handleAddNewTab()
      return
    }

    // 3. Caso seja alguma URL externa nova, deixa na aba a URL aberta e o ícone de globe do lucide
    let finalExternalUrl = targetUrl
    if (!finalExternalUrl.startsWith("http://") && !finalExternalUrl.startsWith("https://") && !finalExternalUrl.startsWith("/")) {
      finalExternalUrl = "https://" + finalExternalUrl
    }

    // Se forceNewTab for false e já houver uma guia com essa URL externa exata, foca nela
    if (!forceNewTab) {
      const existingExternalTab = openApps.find(
        (t) => t.app.type === "external" && (t.app.iframeUrl === finalExternalUrl || t.app.name === finalExternalUrl)
      )

      if (existingExternalTab) {
        if (!options?.inBackground) {
          if (options?.openInSplit && activeAppId) {
            setSplitActiveAppId(existingExternalTab.instanceId)
          } else if (splitActiveAppId !== null) {
            if (focusedPane === "left") {
              setActiveAppId(existingExternalTab.instanceId)
            } else {
              setSplitActiveAppId(existingExternalTab.instanceId)
            }
          } else {
            setActiveAppId(existingExternalTab.instanceId)
          }
        }
        return
      }
    }

    const instanceId = `ext-${Math.random().toString(36).substring(2, 9)}`
    const newTab: OpenAppTab = {
      instanceId,
      pinned: false,
      groupId: null,
      app: {
        id: `external-${instanceId}`,
        name: finalExternalUrl,
        slug: `ext-${instanceId}`,
        description: finalExternalUrl,
        iconUrl: "globe",
        iframeUrl: finalExternalUrl,
        type: "external"
      }
    }

    setOpenApps((prev) => [...prev, newTab])

    if (!options?.inBackground) {
      if (options?.openInSplit && activeAppId) {
        setSplitActiveAppId(instanceId)
      } else if (splitActiveAppId !== null) {
        if (focusedPane === "left") {
          setActiveAppId(instanceId)
        } else {
          setSplitActiveAppId(instanceId)
        }
      } else {
        setActiveAppId(instanceId)
      }
    }
  }, [installedApps, openApps, activeAppId, splitActiveAppId, focusedPane, handleOpenApp, handleAddNewTab, handleOpenSettingsTab, handleOpenProfileTab, handleOpenMarketplaceTab, setOpenApps, setActiveAppId, setSplitActiveAppId])

  return {
    iframeSubpaths,
    updateAppSubpath,
    resolveIframeUrl,
    handleOpenSettingsTab,
    handleOpenProfileTab,
    handleOpenMarketplaceTab,
    handleAddNewTab,
    handleOpenApp,
    handleOpenExternalUrl,
    handleOpenWidgetAsApp,
    handleCloseApp,
    handleCloseAllApps
  }
}
